import { PREVIEW_MODE, EXTERNAL_REDIRECT_TIMEOUT } from '$lib/global/constants';

import { goto } from '$app/navigation';
import { preloadCode, preloadData } from '$app/navigation';
import { base } from '$app/paths';
import { createExternalUrl } from '$lib/helper';
import { disabledNavigation } from '$lib/Stores/stores';
import { createGoToUrlWithPreservedQueries } from '$lib/utils/createGoToUrlWithPreservedQueries';
import { scrollNavigation } from '$lib/utils/scrollToComponent';

import debug from 'debug';
import { get } from 'svelte/store';

import { getResultPageId } from './get-result-page-id';

import type { TrackingProperty } from '$lib/types/TrackingProperty';

const debugLog = debug('app:next-page-navigator');

export class NextPageNavigator {
    private readonly campaignId: string;
    private readonly trackingProperties?: TrackingProperty[];
    private readonly firstResultPage: string;
    private readonly pageIdToSlug: {
        [pageId: string]: string;
    };
    private readonly pathTo404: string;

    constructor(
        campaignId: string,
        firstResultPage: string,
        pageIdToSlugMap: { [pageId: string]: string } = {},
        trackingProperties?: TrackingProperty[],
    ) {
        this.campaignId = campaignId;
        this.firstResultPage = firstResultPage;
        this.pageIdToSlug = pageIdToSlugMap;
        this.pathTo404 = `${base}/404`;
        this.trackingProperties = trackingProperties;
    }

    go(nextPage: string) {
        debugLog('-> go', nextPage);

        if (get(disabledNavigation)) {
            return;
        }

        // Next page is a result page
        if (nextPage === 'result') {
            // Get result page with the highest count
            const resultPageId = getResultPageId(this.campaignId, this.firstResultPage);

            // Navigate to result page
            if (resultPageId) {
                return this.gotoSlug(resultPageId);
            }
        }

        // Navigate to regular next page
        if (nextPage) {
            return this.gotoSlug(nextPage);
        }

        return goto(this.pathTo404);
    }

    async gotoSlug(
        pageId: string,
        options?: {
            replaceState?: boolean | undefined;
            noScroll?: boolean | undefined;
            keepFocus?: boolean | undefined;
            state?: any;
            invalidateAll?: boolean | undefined;
        },
    ) {
        if (get(disabledNavigation)) {
            return;
        }

        const slug = this.pageIdToSlug[pageId];

        debugLog('-> gotoSlug', slug);

        if (PREVIEW_MODE) {
            const gotoUrl = new URL(
                slug ? `${base}/${slug}` : `${base}/${pageId}`,
                window.location.origin,
            );

            const { url } = createGoToUrlWithPreservedQueries(gotoUrl.toString());

            return goto(url, { ...options, noScroll: true });
        }

        const navigationPromise = slug
            ? goto(`${base}/${slug}`, options)
            : goto(`${base}/${pageId}`, options);

        await navigationPromise;

        // Force scroll to top after navigation completes.
        // Double rAF ensures the browser has fully laid out the new page before scrolling,
        // which fixes iOS Safari ignoring scrollTo during mid-layout or momentum scrolling.
        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.scrollTo(0, 0);
                    resolve();
                });
            });
        });

        // When embedded in iframe, scroll the parent page to show the top of the funnel
        if (this.isIframe()) {
            const mainElement = document.getElementById('funnel-app-main');

            if (mainElement) {
                mainElement.scrollTop = 0;
            }

            // Notify the parent page about navigation so they can scroll to the top of their page
            window.parent.postMessage({ type: 'funnel:navigate', slug }, '*');
        }

        return navigationPromise;
    }

    getNextPageUrl(nextPage: string): string {
        // Next page is a result page
        if (nextPage === 'result') {
            const resultPageId = getResultPageId(this.campaignId, this.firstResultPage);

            if (resultPageId) {
                const slug = this.pageIdToSlug[resultPageId] || resultPageId;

                return `${base}/${slug}`;
            }
        }

        // Regular next page
        if (nextPage) {
            const slug = this.pageIdToSlug[nextPage] || nextPage;

            return `${base}/${slug}`;
        }

        return this.pathTo404;
    }

    isIframe(): boolean {
        return window.self !== window.top;
    }

    goExternal(externalLink: string, isNewTab: boolean = false) {
        if (get(disabledNavigation)) {
            return;
        }

        const url = createExternalUrl(externalLink, this.campaignId, this.trackingProperties);

        // When opening in a new tab, we don't need the timeout since the current page
        // isn't being torn down, so tracking events can complete normally
        if (isNewTab) {
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }

        // This timeout is a workaround to ensure that button click tracking events are sent before
        // the navigation happens and the page is torn down. Unfortunately the tracking request itself
        // is not a Promise as of now.
        setTimeout(() => {
            if (this.isIframe()) {
                window.open(url);
            } else {
                return window.location.assign(url);
            }
        }, EXTERNAL_REDIRECT_TIMEOUT);
    }

    preload(nextPageId: string, isExternal: boolean) {
        if (nextPageId && !isExternal) {
            let pageSlug = this.pageIdToSlug[nextPageId];

            if (nextPageId === 'result') {
                const resultPageId = getResultPageId(this.campaignId, this.firstResultPage);

                pageSlug = resultPageId ? this.pageIdToSlug[resultPageId] : pageSlug;
            }

            void preloadData(`${base}/${pageSlug}`);
            void preloadCode(`${base}/${pageSlug}`);
        }
    }

    /**
     * Navigates to a page and scrolls to a specific component after navigation completes
     * @param pageId - The page ID to navigate to
     * @param componentId - The component ID to scroll to after navigation
     */
    async goToPageAndScroll(pageId: string, componentId: string): Promise<void> {
        if (get(disabledNavigation)) {
            return;
        }

        await this.gotoSlug(pageId);
        await scrollNavigation.scrollAfterNavigation(componentId);
    }
}
